import { Sparkles, MessageSquare, Bot, Code2, ArrowRight } from 'lucide-react';
import { useT } from '@/i18n/provider';
import { buttonVariants } from './ui/Button';
import { trackEvent } from '@/lib/analytics';
import { cn } from '@/lib/utils';

const bullets = [
  {
    key: 'natural',
    icon: MessageSquare,
    color: 'text-info-500',
    bg: 'bg-info-50 dark:bg-info-500/10',
    border: 'border-info-200 dark:border-info-500/20',
  },
  {
    key: 'investigate',
    icon: Bot,
    color: 'text-primary-500',
    bg: 'bg-primary-50 dark:bg-primary-500/10',
    border: 'border-primary-200 dark:border-primary-500/20',
  },
  {
    key: 'lpql',
    icon: Code2,
    color: 'text-success-500',
    bg: 'bg-success-50 dark:bg-success-500/10',
    border: 'border-success-200 dark:border-success-500/20',
  },
] as const;

export function AISearchSEOSection() {
  const t = useT();

  return (
    <section id="ai-log-search" className="py-24 sm:py-32">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="max-w-3xl mx-auto text-center mb-16 animate-on-scroll">
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-primary-50 dark:bg-primary-900/20 border border-primary-200 dark:border-primary-800/30 mb-4">
            <Sparkles className="h-3.5 w-3.5 text-primary-500" />
            <span className="text-xs font-medium text-primary-700 dark:text-primary-300">
              {t('aiSearch.badge')}
            </span>
          </div>
          <h2 className="text-3xl sm:text-4xl font-bold tracking-tight text-gray-900 dark:text-white text-balance">
            {t('aiSearch.title')}
          </h2>
          <p className="mt-4 text-lg text-gray-600 dark:text-gray-400 text-balance">
            {t('aiSearch.subtitle')}
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {bullets.map((bullet, i) => {
            const Icon = bullet.icon;
            return (
              <div
                key={bullet.key}
                className="animate-on-scroll glass-subtle rounded-xl p-6 card-hover"
                style={{ transitionDelay: `${i * 100}ms` }}
              >
                <div
                  className={cn(
                    'w-12 h-12 rounded-xl flex items-center justify-center border mb-4',
                    bullet.bg,
                    bullet.border,
                  )}
                >
                  <Icon className={cn('h-6 w-6', bullet.color)} />
                </div>
                <h3 className="font-semibold text-gray-900 dark:text-white mb-2">
                  {t(`aiSearch.bullet.${bullet.key}.title`)}
                </h3>
                <p className="text-sm text-gray-600 dark:text-gray-400 leading-relaxed">
                  {t(`aiSearch.bullet.${bullet.key}.description`)}
                </p>
              </div>
            );
          })}
        </div>

        <div className="mt-12 text-center animate-on-scroll">
          <a
            href="https://app.logpulse.io/sign-in"
            className={buttonVariants({ variant: 'gradient', size: 'xl' })}
            onClick={() => trackEvent('ai_search_cta_click', { location: 'homepage_section' })}
          >
            {t('aiSearch.cta')}
            <ArrowRight className="h-5 w-5 ml-1" />
          </a>
        </div>
      </div>
    </section>
  );
}
